import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompanyService } from '@/services/company/companyService';
import { Company, CreateCompanyRequest } from '@/types/company';
import { ApiError } from '@/types/carAuctionClient';

export const useCompany = (searchQuery?: string) => {
  const queryClient = useQueryClient();

  // Get company by org number (for registration flow - public use only)
  const companyByOrgNumberQuery = useQuery<Company | null, ApiError>({
    queryKey: ['companyByOrgNumber', searchQuery],
    queryFn: async () => {
      if (!searchQuery || !searchQuery.trim()) {
        return null;
      }
        return await CompanyService.getCompanyByOrgNumber(searchQuery.trim());
      
    },
    enabled: Boolean(searchQuery && searchQuery.trim()),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 404 errors (company not found)
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: 0, // Don't delay retries
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Create company mutation (public use - for registration)
  const createCompanyMutation = useMutation({
    mutationFn: (data: CreateCompanyRequest) => CompanyService.createCompany(data),
    onSuccess: () => {
      // Invalidate both public and admin company queries
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['companyManagement'] });
    },
  });

  return {
    // Data
    company: companyByOrgNumberQuery.data, // Single company for org number search
    companies: companyByOrgNumberQuery.data ? [companyByOrgNumberQuery.data] : [], // For compatibility
    
    // Loading states
    isLoadingCompanies: companyByOrgNumberQuery.isLoading || companyByOrgNumberQuery.isFetching,
    
    // Error states
    companiesError: companyByOrgNumberQuery.error,
    
    // Refetch functions
    refetchCompanies: companyByOrgNumberQuery.refetch,

    // Create company mutation
    createCompany: createCompanyMutation.mutate,
    createCompanyAsync: createCompanyMutation.mutateAsync,
    isCreatingCompany: createCompanyMutation.isPending,
    createCompanyError: createCompanyMutation.error,
  };
};
